import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ChildTeamInfo {
  team_id: string;
  team_name: string;
  sport: string;
  coach_name: string;
}

export interface CoachContentItem {
  id: string;
  title: string;
  type: string;
  created_at: string;
  viewed: boolean;
}

export interface ChildDashboard {
  xp: number;
  streak: number;
  level: number;
  completed_lessons: string[];
  last_active_date: string | null;
  coach_content: CoachContentItem[];
  achievements: { achievement_id: string; earned_at: string }[];
}

export interface NotificationPrefs {
  weekly_report: boolean;
  coach_content: boolean;
  streak_reminders: boolean;
  milestone_alerts: boolean;
}

const DEFAULT_PREFS: NotificationPrefs = {
  weekly_report: true,
  coach_content: true,
  streak_reminders: true,
  milestone_alerts: true,
};

/** Loads everything the Parent Dashboard needs for one selected child. */
export function useChildDashboard(childUserId: string | null) {
  const [data, setData] = useState<ChildDashboard | null>(null);
  const [team, setTeam] = useState<ChildTeamInfo | null>(null);
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!childUserId) { setData(null); setTeam(null); setLoading(false); return; }
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();

    const [dash, teamRes, prefRes] = await Promise.all([
      supabase.rpc('get_child_dashboard' as any, { p_child_user_id: childUserId }),
      supabase.rpc('get_child_team_info' as any, { p_child_user_id: childUserId }),
      user
        ? (supabase.from('notification_preferences') as any)
            .select('weekly_report, coach_content, streak_reminders, milestone_alerts')
            .eq('parent_user_id', user.id)
            .eq('child_player_id', childUserId)
            .maybeSingle()
        : Promise.resolve({ data: null }),
    ]);

    setData((dash.data as any) ?? null);
    const t = Array.isArray(teamRes.data) ? teamRes.data[0] : null;
    setTeam((t as any) ?? null);
    setPrefs(((prefRes as any)?.data as NotificationPrefs) ?? DEFAULT_PREFS);
    setLoading(false);
  }, [childUserId]);

  useEffect(() => { load(); }, [load]);

  const savePrefs = useCallback(async (next: NotificationPrefs) => {
    setPrefs(next);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !childUserId) return;
    await (supabase.from('notification_preferences') as any).upsert(
      { parent_user_id: user.id, child_player_id: childUserId, ...next },
      { onConflict: 'parent_user_id,child_player_id' },
    );
  }, [childUserId]);

  const joinTeam = useCallback(async (code: string) => {
    if (!childUserId) return { error: 'No child selected' };
    const { data: res, error } = await supabase.rpc('parent_join_child_to_team' as any, {
      p_child_user_id: childUserId,
      p_code: code.trim().toLowerCase(),
    });
    if (error) return { error: error.message };
    const r = res as any;
    if (r?.error) return { error: r.error as string };
    await load();
    return { team_name: r?.team_name as string };
  }, [childUserId, load]);

  return { data, team, prefs, loading, savePrefs, joinTeam, refetch: load };
}
